<script lang="ts" setup>
import {
  computed,
  onMounted,
  ref,
  toRefs,
} from 'vue';

import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';

import MessageAttachmentPreview from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-preview/MessageAttachmentPreview.vue';

interface MessageAttachmentProps {
  attachments: MessageAttachmentDTO[];
  isMyMessage: boolean;
  conversationId: string;
  messageId: string;
}

const props = defineProps<MessageAttachmentProps>();

const { attachments } = toRefs(props);

const gridCols = ref(1);

const firstAttachmentColSpan = computed(() => {
  if (gridCols.value === 2) {
    return (attachments.value.length % gridCols.value) + 1;
  }

  return 1;
});

const SINGLE_ATTACHMENT_SIZE = 350;
const MULTIPLE_ATTACHMENTS_SIZE = 170;

function attachmentSize(attachmentIndex: number) {
  if (attachmentIndex !== 0) {
    return MULTIPLE_ATTACHMENTS_SIZE;
  }

  const isSingleAttachment = attachments.value.length === 1;
  const firstAttachmentSpansTwoColumns = firstAttachmentColSpan.value === 2;

  if (isSingleAttachment || firstAttachmentSpansTwoColumns) {
    return SINGLE_ATTACHMENT_SIZE;
  }

  return MULTIPLE_ATTACHMENTS_SIZE;
}

onMounted(() => {
  if (attachments.value.length > 2) {
    gridCols.value = 2;
  }
  else {
    gridCols.value = attachments.value.length;
  }
});
</script>

<template>
  <div
    :class="{
      'grid-cols-1': gridCols === 1,
      'grid-cols-2': gridCols === 2,
    }"
    class="grid overflow-hidden gap-2 max-w-[350px] w-full"
  >
    <MessageAttachmentPreview
      v-for="(attachment, ix) in attachments"
      :key="attachment.id"
      :class="[ix === 0 && firstAttachmentColSpan === 2 ? 'col-span-2' : '']"
      :attachment="attachment"
      :is-my-message="isMyMessage"
      :max-size="attachmentSize(ix)"
      :conversation-id="props.conversationId"
      :message-id="props.messageId"
    />
  </div>
</template>
